/**
 * Матрица «роль × отчёт».
 *
 * Клетка перебирает три состояния: пусто -> разрешено -> запрещено -> пусто.
 * Пусто и запрет — разные вещи, и это должно быть видно: по умолчанию
 * запрещено, но явный запрет ещё и перекрывает разрешения других ролей.
 */

import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { api, ApiError } from '../api'
import type { AccessMatrix, ScopeDimension, ScopeRule, SectionCatalog } from '../types'
import { Badge, Card, Loading, Notice, TableWrap } from '../components/ui'
import { Icon } from '../components/Icon'
import { pluralize } from '../format'
import { ScopeRuleForm, describePeriodRule } from './AdminUsers'

type Mode = 'allow' | 'deny' | null

const NEXT_MODE: Record<string, Mode> = { none: 'allow', allow: 'deny', deny: null }

export function AdminAccess() {
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  // Двойной клик по клетке — один шаг, а не два: второй приходит раньше перерисовки.
  const savingNow = useRef(new Set<string>())
  const [roleId, setRoleId] = useState<number | null>(null)
  // Отчёт выдан, но срез прав человека к нему неприменим — у него он
  // откроется отказом. Это видно прямо в матрице, а не в жалобе.
  // Считается по итоговым правам каждого сотрудника (роль + личные правила).
  const [conflicts, setConflicts] = useState<Map<string, { names: string[]; roles: Set<number> }>>(new Map())

  useEffect(() => {
    if (!matrix) return
    // Сервер считает это сам, одним ответом (unavailable в access-matrix):
    // раньше здесь был запрос итоговых прав на каждого сотрудника.
    const result = new Map<string, { names: string[]; roles: Set<number> }>()
    for (const [code, holders] of Object.entries(matrix.unavailable ?? {})) {
      result.set(code, {
        names: holders.map((holder) => holder.full_name),
        roles: new Set(holders.flatMap((holder) => holder.role_ids)),
      })
    }
    setConflicts(result)
  }, [matrix])

  const load = () =>
    api
      .accessMatrix()
      .then(setMatrix)
      .catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить матрицу'))

  useEffect(() => {
    void load()
  }, [])

  if (error) return <Notice tone="bad">{error}</Notice>
  if (!matrix) return <Loading rows={6} />

  const toggle = async (roleId: number, reportCode: string, current: Mode) => {
    const key = `${roleId}:${reportCode}`
    if (savingNow.current.has(key)) return
    savingNow.current.add(key)
    setSaving(key)

    try {
      const next = NEXT_MODE[current ?? 'none']!
      await api.setReportAccess({
        subject_type: 'role',
        subject_id: String(roleId),
        report_code: reportCode,
        mode: next,
      })
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось изменить доступ')
    } finally {
      savingNow.current.delete(key)
      setSaving(null)
    }
  }

  const categories = [...new Set(matrix.reports.map((report) => report.category))]

  return (
    <div className="stack" style={{ gap: 18 }}>
      <Notice tone="info">
        По умолчанию запрещено: пустая клетка означает, что отчёт недоступен. Роли складываются, а явный
        запрет сильнее любого числа разрешений из других ролей.
      </Notice>

      <Card flush>
        <TableWrap>
          <table className="data matrix">
            <thead>
              <tr>
                <th style={{ minWidth: 260 }}>Отчёт</th>
                {matrix.roles.map((role) => (
                  <th key={role.id} style={{ textAlign: 'center' }} title={role.description ?? undefined}>
                    <button
                      type="button"
                      className={`matrix__role${roleId === role.id ? ' matrix__role--on' : ''}`}
                      onClick={() => setRoleId(role.id)}
                      aria-pressed={roleId === role.id}
                    >
                      {role.title}
                    </button>
                    <div className="tiny muted matrix__count">
                      {pluralize(role.users_count, 'человек', 'человека', 'человек')}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <Fragment key={`cat-${category}`}>
                  <tr>
                    <td colSpan={matrix.roles.length + 1} className="matrix__category">
                      {category}
                    </td>
                  </tr>
                  {matrix.reports
                    .filter((report) => report.category === category)
                    .map((report) => (
                      <tr key={report.code}>
                        <td>
                          <div>{report.title}</div>
                          <div className="tiny muted">
                            {pluralize(report.metrics.length, 'показатель', 'показателя', 'показателей')}
                          </div>
                          {conflicts.get(report.code) && (
                            <div className="tiny warning-text">
                              Не откроется у {pluralize(conflicts.get(report.code)!.names.length, 'человека', 'человек', 'человек')}:{' '}
                              {conflicts.get(report.code)!.names.join(', ')} — их срез прав к отчёту неприменим
                            </div>
                          )}
                        </td>
                        {matrix.roles.map((role) => {
                          const cell = role.access[report.code]
                          const mode: Mode = cell?.mode ?? null
                          const key = `${role.id}:${report.code}`

                          return (
                            <td key={role.id} style={{ textAlign: 'center' }}>
                              <button
                                className={`matrix-cell${mode ? ` matrix-cell--${mode}` : ''}${
                                  mode === 'allow' && conflicts.get(report.code)?.roles.has(role.id) ? ' matrix-cell--conflict' : ''
                                }`}
                                disabled={saving === key}
                                onClick={() => toggle(role.id, report.code, mode)}
                                title={
                                  mode === 'allow'
                                    ? 'Разрешено. Нажмите, чтобы запретить'
                                    : mode === 'deny'
                                      ? 'Запрещено явно. Нажмите, чтобы убрать правило'
                                      : 'Недоступно по умолчанию. Нажмите, чтобы разрешить'
                                }
                              >
                                {mode === 'allow' ? (
                                  <>
                                    <Icon name="check" size={14} label={cell?.allowed_metrics ? 'Разрешено частично' : 'Разрешено'} />
                                    {cell?.allowed_metrics && <span className="matrix-cell__part" aria-hidden="true" />}
                                  </>
                                ) : mode === 'deny' ? (
                                  <Icon name="x" size={14} label="Запрещено" />
                                ) : null}
                              </button>
                            </td>
                          )
                        })}
                      </tr>
                    ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </TableWrap>
      </Card>

      <div className="row">
        <Badge tone="good"><Icon name="check" size={12} /> разрешено</Badge>
        <Badge tone="good"><Icon name="check" size={12} /> с точкой — не все показатели</Badge>
        <Badge tone="bad"><Icon name="x" size={12} /> запрещено явно</Badge>
        <Badge>пусто — недоступно по умолчанию</Badge>
        <span className="tiny muted">Нажмите на название роли, чтобы настроить её разделы, показатели и срез.</span>
      </div>

      {roleId !== null && matrix.roles.some((role) => role.id === roleId) && (
        <RoleSettings matrix={matrix} roleId={roleId} onClose={() => setRoleId(null)} />
      )}
    </div>
  )
}

/**
 * Настройка роли сверх матрицы: какие разделы меню скрыть, какие показатели
 * в выданных отчётах не показывать, какой срез данных у всей роли.
 */
function RoleSettings({ matrix, roleId, onClose }: { matrix: AccessMatrix; roleId: number; onClose: () => void }) {
  const role = matrix.roles.find((item) => item.id === roleId)!
  const subject = String(roleId)
  const [catalog, setCatalog] = useState<SectionCatalog | null>(null)
  const [hidden, setHidden] = useState<{ metrics: Record<string, string[]>; sections: string[] } | null>(null)
  const [rules, setRules] = useState<ScopeRule[]>([])
  const [dimensions, setDimensions] = useState<ScopeDimension[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const running = useRef(new Set<string>())

  const load = useCallback(async () => {
    const [sections, hiddenResponse, rulesResponse, dims] = await Promise.all([
      api.sectionCatalog(),
      api.hidden('role', subject),
      api.scopeRules('role', subject),
      api.scopeDimensions(),
    ])
    setCatalog(sections)
    setHidden(hiddenResponse)
    setRules(rulesResponse.rules)
    setDimensions(dims.dimensions)
  }, [subject])

  useEffect(() => {
    setHidden(null)
    load().catch((caught) => setError(caught instanceof ApiError ? caught.message : 'Не удалось загрузить настройки роли'))
  }, [load])

  const run = async (key: string, action: () => Promise<unknown>) => {
    if (running.current.has(key)) return
    running.current.add(key)
    setBusy(key)
    setError(null)
    try {
      await action()
      await load()
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Не удалось сохранить')
    } finally {
      running.current.delete(key)
      setBusy(null)
    }
  }

  const allowedReports = matrix.reports.filter((report) => role.access[report.code]?.mode === 'allow')

  return (
    <Card
      title={`Роль «${role.title}» · ${pluralize(role.users_count, 'человек', 'человека', 'человек')}`}
      actions={
        <button className="btn btn--sm btn--ghost" onClick={onClose}>
          Закрыть
        </button>
      }
    >
      {error && (
        <div style={{ marginBottom: 12 }}>
          <Notice tone="bad">{error}</Notice>
        </div>
      )}
      {!catalog || !hidden ? (
        <Loading rows={4} />
      ) : (
        <div className="stack" style={{ gap: 20 }}>
          <div>
            <div className="field__label" style={{ marginBottom: 6 }}>
              Разделы меню. Раздел виден, если роли выдан его отчёт; здесь его можно скрыть для всей роли.
            </div>
            {catalog.groups.map((group) => {
              const groupHidden = hidden.sections.includes(group.code)
              return (
                <div className="access-group" key={group.code}>
                  <div className="access-group__head">
                    <span className="access-group__title">{group.title}</span>
                    <button
                      className="btn btn--sm"
                      disabled={busy === group.code}
                      onClick={() =>
                        run(group.code, () =>
                          api.setSectionHidden({ subject_type: 'role', subject_id: subject, section_code: group.code, hidden: !groupHidden }),
                        )
                      }
                    >
                      {groupHidden ? 'Вернуть раздел' : 'Скрыть весь раздел'}
                    </button>
                  </div>
                  <div className="chip-group">
                    {group.sections.map((section) => {
                      const off = groupHidden || hidden.sections.includes(section.code)
                      return (
                        <button
                          key={section.code}
                          type="button"
                          className={`metric-chip${off ? ' metric-chip--hidden' : ''}`}
                          aria-pressed={off}
                          disabled={groupHidden || busy === section.code}
                          title={`Построен на: ${section.reports.join(', ')}`}
                          onClick={() =>
                            run(section.code, () =>
                              api.setSectionHidden({ subject_type: 'role', subject_id: subject, section_code: section.code, hidden: !off }),
                            )
                          }
                        >
                          <span className="metric-chip__mark" aria-hidden="true">
                            {off ? '○' : '●'}
                          </span>
                          {section.title}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )
            })}
            <div className="tiny muted" style={{ marginTop: 8 }}>
              Скрытие убирает экран, а не цифры: если отчёт под разделом выдан, он остаётся в «Дополнительных отчётах».
            </div>
          </div>

          <div>
            <div className="field__label" style={{ marginBottom: 6 }}>
              Показатели в выданных отчётах. Скрытый у роли показатель не видит никто с этой ролью — даже если другая роль
              его даёт.
            </div>
            {allowedReports.length === 0 ? (
              <div className="tiny muted">Роли не выдан ни один отчёт — отметьте клетки в матрице выше.</div>
            ) : (
              <div className="access-list">
                {allowedReports.map((report) => {
                  const hiddenHere = hidden.metrics[report.code] ?? []
                  const listed = role.access[report.code]?.allowed_metrics ?? null
                  return (
                    <div className="access-row" key={report.code}>
                      <div className="access-row__main">
                        <div className="access-row__title">{report.title}</div>
                        {listed && (
                          <div className="access-row__note">
                            Роли выдан только перечень: {listed.map((key) => report.metrics.find((item) => item.key === key)?.title ?? key).join(', ')}.
                          </div>
                        )}
                        <div className="metric-chips">
                          {report.metrics.map((metric) => {
                            const off = hiddenHere.includes(metric.key)
                            const key = `${report.code}:${metric.key}`
                            return (
                              <button
                                key={metric.key}
                                type="button"
                                className={`metric-chip${off ? ' metric-chip--hidden' : ''}`}
                                aria-pressed={off}
                                disabled={busy === key}
                                onClick={() =>
                                  run(key, () =>
                                    api.setMetricHidden({ subject_type: 'role', subject_id: subject, report_code: report.code, metric: metric.key, hidden: !off }),
                                  )
                                }
                              >
                                <span className="metric-chip__mark" aria-hidden="true">
                                  {off ? '○' : '●'}
                                </span>
                                {metric.title}
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div>
            <div className="field__label" style={{ marginBottom: 6 }}>
              Срез данных для всей роли. Обычно срез задают лично (свой город у каждого директора), а для роли — общее
              ограничение, например глубину истории.
            </div>
            {rules.length === 0 ? (
              <div className="tiny muted" style={{ marginBottom: 10 }}>
                Ограничений у роли нет.
              </div>
            ) : (
              <div className="access-list" style={{ marginBottom: 10 }}>
                {rules.map((rule) => (
                  <div className="access-row" key={rule.id}>
                    <div className="access-row__main">
                      <div className="access-row__title">
                        {rule.dimension_title}:{' '}
                        <span style={{ fontWeight: 400 }}>
                          {rule.dimension === 'period'
                            ? describePeriodRule(rule)
                            : `${rule.mode === 'allow' ? 'только' : 'кроме'} ${rule.labels.join(', ') || rule.values.join(', ')}`}
                        </span>
                      </div>
                    </div>
                    <div className="access-row__side">
                      <button
                        className="btn btn--sm btn--ghost btn--danger"
                        disabled={busy === `rule-${rule.id}`}
                        onClick={() => run(`rule-${rule.id}`, () => api.deleteScopeRule(rule.id))}
                      >
                        Снять
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ScopeRuleForm
              userId={subject}
              subjectType="role"
              dimensions={dimensions}
              onCreated={load}
              onError={setError}
            />
          </div>
        </div>
      )}
    </Card>
  )
}
